/**
 * Smart column-name → Mockaroo field-type suggester.
 *
 * When a user uploads a CSV with just a header row, we use these rules to
 * pre-fill a sensible Mockaroo type per column. The user can override
 * anything; this just saves them a first pass of clicking through 13
 * dropdowns for a sites-template CSV.
 *
 * Rules are evaluated in order — first match wins. Keep more specific
 * patterns above more general ones (e.g. "called_id" before "id").
 */

import type { ColumnSchema } from './api';

export interface SmartGuess {
  type: string;
  options?: Record<string, unknown>;
}

const RULES: Array<{ pattern: RegExp; guess: SmartGuess }> = [
  // Telecom-specific (matches 8x8 site/extension/user templates)
  { pattern: /timezone|^tz$|tz_id|time[\s_-]?zone/i,
    guess: { type: 'Time Zone' } },
  { pattern: /main[\s_-]?line|called[\s_-]?id|caller[\s_-]?id|phone|tel(?:ephone)?|fax/i,
    guess: { type: 'Regular Expression', options: { value: '\\+1[2-9][0-9]{9}' } } },
  { pattern: /^ext$|extension|ext[\s_-]?length/i,
    guess: { type: 'Number', options: { min: 3, max: 5 } } },
  { pattern: /pbx|switch[\s_-]?name/i,
    guess: { type: 'Company Name' } },
  { pattern: /calling[\s_-]?plan|plan[\s_-]?type/i,
    guess: {
      type: 'Custom List',
      options: { values: 'Domestic\nNorth America\nGlobal\nNational' },
    } },
  { pattern: /^language$|lang(?:uage)?[\s_-]?code/i,
    guess: { type: 'Language' } },

  // Boolean-ish flags
  { pattern: /^use[\s_-]|^is[\s_-]|^has[\s_-]|enabled|disabled|active|shared|deleted|suggested/i,
    guess: { type: 'Boolean' } },

  // Identity
  { pattern: /\b(?:^id$|_id$|^uid$|^uuid$|guid)/i,
    guess: { type: 'GUID' } },
  { pattern: /^row[\s_-]?(?:number|num|n)$/i,
    guess: { type: 'Row Number' } },
  { pattern: /^seq(?:uence)?$/i,
    guess: { type: 'Sequence', options: { start: 1, step: 1 } } },

  // People
  { pattern: /first[\s_-]?name|fname|given[\s_-]?name/i,
    guess: { type: 'First Name' } },
  { pattern: /last[\s_-]?name|lname|surname|family[\s_-]?name/i,
    guess: { type: 'Last Name' } },
  { pattern: /full[\s_-]?name|^name$|user[\s_-]?name|display[\s_-]?name/i,
    guess: { type: 'Full Name' } },
  { pattern: /e?mail/i,
    guess: { type: 'Email Address' } },
  { pattern: /username|login/i,
    guess: { type: 'Username' } },

  // Location
  { pattern: /country[\s_-]?code|^cc$/i,
    guess: { type: 'Country Code' } },
  { pattern: /^country$/i,
    guess: { type: 'Country' } },
  { pattern: /^city$|town/i,
    guess: { type: 'City' } },
  { pattern: /state|province|region/i,
    guess: { type: 'State' } },
  { pattern: /address|street/i,
    guess: { type: 'Street Address' } },
  { pattern: /postal|zip[\s_-]?code|^zip$/i,
    guess: { type: 'Postal Code' } },
  { pattern: /lat(?:itude)?/i,
    guess: { type: 'Latitude' } },
  { pattern: /lon(?:gitude)?|lng/i,
    guess: { type: 'Longitude' } },

  // Site/company
  { pattern: /site[\s_-]?name|location[\s_-]?name|office[\s_-]?name|branch/i,
    guess: { type: 'Company Name' } },
  { pattern: /company|organization|account[\s_-]?name/i,
    guess: { type: 'Company Name' } },
  { pattern: /department|dept/i,
    guess: { type: 'Department' } },
  { pattern: /title|role|position/i,
    guess: { type: 'Job Title' } },
  { pattern: /currency/i,
    guess: { type: 'Currency Code' } },

  // Dates
  { pattern: /^date$|created[\s_-]?at|updated[\s_-]?at|date$|^dob$|birth/i,
    guess: {
      type: 'Date',
      options: { min: '2020-01-01', max: '2026-12-31', format: '%Y-%m-%d' },
    } },
  { pattern: /time$|timestamp/i,
    guess: { type: 'Datetime' } },

  // Generic codes — usually a custom list
  { pattern: /^code$|^type$|category|status/i,
    guess: { type: 'Custom List', options: { values: 'A\nB\nC' } } },

  // Numbers
  { pattern: /count|qty|quantity|amount|total|number/i,
    guess: { type: 'Number', options: { min: 0, max: 1000 } } },
];

export function guessFieldType(columnName: string): SmartGuess {
  const trimmed = columnName.trim();
  for (const rule of RULES) {
    if (rule.pattern.test(trimmed)) return rule.guess;
  }
  // Fallback — short words. Mockaroo never returns empty for this.
  return { type: 'Words', options: { min: 1, max: 2 } };
}

/**
 * Apply smart mapping to an array of column names. Returns a draft
 * ColumnSchema[] the user can refine in the UI.
 */
export function buildSchemaFromHeaders(headers: string[]): ColumnSchema[] {
  return headers.map((name) => {
    const guess = guessFieldType(name);
    return {
      name,
      type: guess.type,
      options: guess.options ?? {},
    };
  });
}
